import Icon from "@components/Atoms/Icon";
import { ItemType } from "@core/FileStructue/Item/ItemType";
import { useNavTreePersistence } from "@core/SitePresenter/NavTreeStateManager";
import useHandleItemClick from "@core-ui/hooks/useHandleItemClick";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import EditMenu from "@ext/item/EditMenu";
import t from "@ext/localization/locale/translate";
import NavigationDropdown from "@ext/navigation/components/NavigationDropdown";
import { Button } from "@ui-kit/Button";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { ArticleLink, CategoryLink, ItemLink, LinkFilter } from "../../NavigationLinks";
import IconExtension from "../main/render/IconExtension";
import LevNavItem from "../main/render/Item";

const LevNavWatchTree = React.memo(
	({ items, closeNavigation }: { items: ItemLink[]; closeNavigation?: () => void }) => {
		const catalogName = useCatalogPropsStore((state) => state.data?.name);
		const { effectiveItems, handleToggle } = useNavTreePersistence(catalogName, items);

		return (
			<div>
				<Tree closeNavigation={closeNavigation} items={effectiveItems} level={0} onToggleItem={handleToggle} />
			</div>
		);
	},
	(prevProps, nextProps) => {
		return prevProps.items === nextProps.items;
	},
);

interface WatchTreeProps {
	items: ItemLink[];
	level: number;
	filter?: LinkFilter;
	closeNavigation?: () => void;
	onToggleItem?: (refPath: string, isOpen: boolean) => void;
}

const Tree = ({ items, level, filter, closeNavigation, onToggleItem }: WatchTreeProps) => {
	const [isFiltered, setFiltered] = useState(true);
	return (
		<ul className={level === 0 ? "tree-root" : ""}>
			{items.map((item, key) => {
				return !isFiltered || isVisible(filter, item as ArticleLink, key, items.length) ? (
					<Item
						closeNavigation={closeNavigation}
						item={item}
						// biome-ignore lint/suspicious/noArrayIndexKey: watchtree is readonly and not reorderable, so index is fine as key
						key={key}
						level={level}
						onToggleItem={onToggleItem}
					/>
				) : null;
			})}
			{filter && isFiltered ? (
				<li onClick={() => setFiltered(false)}>
					<LevNavItem
						leftExtensions={<Icon code="ellipsis" svgStyle={{ fill: "currentColor" }} />}
						level={level}
						onClick={closeNavigation}
					/>
				</li>
			) : null}
		</ul>
	);
};

const Item = ({
	item,
	level,
	closeNavigation,
	onToggleItem,
}: {
	item: ItemLink;
	level: number;
	closeNavigation: () => void;
	onToggleItem?: (refPath: string, isOpen: boolean) => void;
}) => {
	const ref = useRef<HTMLLIElement>(null);
	const [scrollFlag, setScrollFalag] = useState(true);
	const [isHover, setIsHover] = useState(false);

	const isActive = item.isCurrentLink;

	const isCategory = item.type === ItemType.category && (item as CategoryLink)?.items?.length !== 0;
	const existsContent = item.type === ItemType.category ? (item as CategoryLink).existContent : true;

	const [isOpen, setIsOpen] = useState(level === 0 || (item as CategoryLink).isExpanded);

	const onToggle = () => {
		const newIsOpen = !isOpen;
		setIsOpen(newIsOpen);
		if (isCategory) onToggleItem?.(item.ref.path, newIsOpen);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: was this way before,  either need to fix or intentionally not include onToggle in deps
	const toggleFunction = useCallback(() => {
		if (isActive || !isOpen) onToggle();
		// biome-ignore lint/correctness/useExhaustiveDependencies: above
	}, [onToggle]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: the same with onToggle, need to verify if this is intentional or not
	const handleCloseNavigation = useCallback(() => {
		if (existsContent || item.type === ItemType.article) closeNavigation?.();
	}, [closeNavigation]);

	const handleClick = useHandleItemClick({
		isCurrentLink: isActive,
		itemPath: item.pathname,
		closeNavigation: handleCloseNavigation,
		toggleFunction,
	});

	useEffect(() => {
		if (ref.current && scrollFlag && isActive)
			ref.current.scrollIntoView({ behavior: "auto", inline: "center", block: "center" });
		setScrollFalag(false);
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: was this way before, either need to fix or intentionally not include handleClick in deps
	useEffect(() => {
		if ((item as CategoryLink).isExpanded) setIsOpen((item as CategoryLink).isExpanded);
	}, [(item as CategoryLink).isExpanded]);

	return (
		<li ref={ref}>
			<LevNavItem
				isCategory={isCategory}
				isHover={isHover}
				isOpen={isOpen}
				item={item}
				leftExtensions={<IconExtension item={item} />}
				level={level}
				onClick={handleClick}
				onToggle={onToggle}
				rightExtensions={
					<NavigationDropdown
						style={{ marginRight: "-3px" }}
						tooltipText={t("article.actions.title")}
						trigger={
							<Button className="p-0 h-full" size="xs" variant="text">
								<Icon code="ellipsis-vertical" />
							</Button>
						}
					>
						<EditMenu
							itemLink={item}
							onClose={() => setIsHover(false)}
							onOpen={() => setIsHover(true)}
							setItemLink={() => {}}
						/>
					</NavigationDropdown>
				}
			/>
			{isCategory && isOpen && (
				<Tree
					closeNavigation={closeNavigation}
					filter={(item as CategoryLink).filter}
					items={(item as CategoryLink).items}
					level={level + 1}
					onToggleItem={onToggleItem}
				/>
			)}
		</li>
	);
};

function isVisible(filter: LinkFilter, articleLink: ArticleLink, idx: number, count: number): boolean {
	if (!filter) return true;
	if (articleLink.alwaysShow) return true;
	if (filter.top && filter.top > idx) return true;
	if (filter.last && filter.last >= count - idx) return true;
	return false;
}

export default LevNavWatchTree;
